/**
 * Stage 3 — per-column room-interval extraction (pure).
 *
 * A room interval pairs a floor surface with the nearest ceiling surface at a
 * plausible room height above it, requiring the space between to be (nearly)
 * empty. The gap test ignores the furniture zone just above the floor and the
 * clearance zone just below the ceiling, so tables and lamps don't veto real
 * rooms. Two slab faces can never pair (their gap is below MIN_ROOM_HEIGHT):
 * the slab bottom becomes the ceiling of the room below, the slab top the
 * floor of the room above — which is exactly what divides storeys.
 *
 * Candidates are additionally strength-gated: a surface far weaker than the
 * column's strongest surface (wardrobe top, door frame, soffit) is skipped as
 * a floor/ceiling candidate — otherwise the nearest-partner search latches
 * onto it while the obviously better surface in the same column goes unused.
 * Skipped surfaces keep their intermediate-veto role.
 */

import type { ColumnInterval, ColumnSurface, SurfaceRole } from './types';
import type { FloorDetectionTuning } from './tuning';

export interface ColumnIntervalResult {
  intervals: ColumnInterval[];
  /** Parallel to the input surfaces — debug classification. */
  roles: SurfaceRole[];
}

/**
 * Extract room intervals from one column's surfaces.
 *
 * @param surfaces Ascending-by-z surfaces of the column.
 * @param bins     The column's z-histogram (for the gap emptiness test).
 * @param binSize  Bin height in metres.
 * @param z0       World z of the bottom edge of bin 0.
 */
export function extractColumnIntervals(surfaces: ColumnSurface[], bins: Uint16Array, binSize: number, z0: number, tuning: FloorDetectionTuning): ColumnIntervalResult {
  const intervals: ColumnInterval[] = [];
  const roles: SurfaceRole[] = surfaces.map(() => 'unused');

  /** Raw points strictly inside (zLo, zHi); 0 when the zone is empty or inverted. */
  const countBetween = (zLo: number, zHi: number): number => {
    if (zHi <= zLo) return 0;
    const loBin = Math.max(0, Math.ceil((zLo - z0) / binSize));
    const hiBin = Math.min(bins.length - 1, Math.floor((zHi - z0) / binSize) - 1);
    let sum = 0;
    for (let i = loBin; i <= hiBin; i++) sum += bins[i];
    return sum;
  };

  let maxSurfaceCount = 0;
  for (const surface of surfaces) maxSurfaceCount = Math.max(maxSurfaceCount, surface.count);
  const minCandidateCount = tuning.CANDIDATE_STRENGTH_FACTOR * maxSurfaceCount;

  let lastAcceptedCeilingZ: number | null = null;

  for (let a = 0; a < surfaces.length; a++) {
    const floor = surfaces[a];
    // Too weak next to the column's strongest surface — never a floor.
    if (floor.count < minCandidateCount) continue;
    // Surfaces inside an accepted room (furniture) are not floor candidates.
    if (lastAcceptedCeilingZ !== null && floor.z < lastAcceptedCeilingZ + tuning.MIN_SLAB_SEPARATION) continue;

    for (let b = a + 1; b < surfaces.length; b++) {
      const ceiling = surfaces[b];
      // A weak nearest surface must not end (or win) the ceiling search —
      // skip it and keep looking upward for a strong ceiling.
      if (ceiling.count < minCandidateCount) continue;
      const height = ceiling.z - floor.z;
      if (height < tuning.MIN_ROOM_HEIGHT) continue; // slab partner / shelf — not a ceiling
      if (height > tuning.MAX_ROOM_HEIGHT) break; // give up on this floor candidate

      const gapLo = floor.z + tuning.FURNITURE_IGNORE_HEIGHT;
      const gapHi = ceiling.z - tuning.CEILING_CLEARANCE;
      const gapCount = countBetween(gapLo, gapHi);
      const budget = Math.max(tuning.GAP_MIN_POINT_BUDGET, tuning.GAP_FRACTION * (floor.count + ceiling.count));

      // A strong surface inside the gap zone means a real floor sits between
      // the candidates (the pairing would span two rooms).
      let intermediateStrong = false;
      for (let k = a + 1; k < b; k++) {
        const mid = surfaces[k];
        if (mid.z > gapLo && mid.z < gapHi && mid.count >= tuning.INTERMEDIATE_SURFACE_FACTOR * Math.min(floor.count, ceiling.count)) {
          intermediateStrong = true;
          break;
        }
      }

      if (gapCount <= budget && !intermediateStrong) {
        intervals.push({ floorZ: floor.z, ceilingZ: ceiling.z, floorCount: floor.count, ceilingCount: ceiling.count });
        roles[a] = 'floor';
        roles[b] = 'ceiling';
        lastAcceptedCeilingZ = ceiling.z;
      } else if (roles[a] === 'unused') {
        // A farther ceiling would only widen a gap that already failed, or
        // skip past a real intermediate floor — stop trying this floor.
        roles[a] = 'rejected';
      }
      break;
    }
  }

  return { intervals, roles };
}
